import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { formatCurrency as marketFormatCurrency, getCurrencySymbol, getIdentifierLabel } from '@/lib/market';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, User, Building, TrendingUp, RefreshCw, Users, UserPlus, CreditCard, Settings } from 'lucide-react';
import PortfolioChart from '@/components/PortfolioChart';
import PortfolioThresholds from '@/components/PortfolioThresholds';
import NoGoList from '@/components/NoGoList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminClientsProspects = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('clients-prospects');
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Portfolio dialog states
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [clientPortfolio, setClientPortfolio] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  
  // Risk profile dialog states
  const [riskProfileDialogOpen, setRiskProfileDialogOpen] = useState(false);
  const [clientRiskProfile, setClientRiskProfile] = useState(null);
  const [riskProfileHistory, setRiskProfileHistory] = useState([]);
  
  // Social profile dialog states
  const [socialProfileDialogOpen, setSocialProfileDialogOpen] = useState(false);
  const [socialProfileData, setSocialProfileData] = useState(null);
  const [socialProfileForm, setSocialProfileForm] = useState({
    education: '',
    travel_preferences: '',
    sports_interests: '',
    philanthropy: '',
    lifestyle_indicators: '',
    social_media_activity: '',
    hobbies: '',
    professional_network: ''
  });
  
  // Rebalancing states
  const [rebalanceDialogOpen, setRebalanceDialogOpen] = useState(false);
  const [rebalanceData, setRebalanceData] = useState(null);
  const [rebalanceLoading, setRebalanceLoading] = useState(false);

  // Portfolio Thresholds states
  const [thresholdsDialogOpen, setThresholdsDialogOpen] = useState(false);
  const [thresholdsClientId, setThresholdsClientId] = useState(null);
  const [thresholdsClientName, setThresholdsClientName] = useState('');

  // Bank hierarchy (branch/region/client_category) state
  const [hierarchyDialogOpen, setHierarchyDialogOpen] = useState(false);
  const [hierarchyClientId, setHierarchyClientId] = useState(null);
  const [hierarchyForm, setHierarchyForm] = useState({ branch: '', region: '', client_category: '' });

  const openHierarchyDialog = (item) => {
    setHierarchyClientId(item.id);
    setHierarchyForm({
      branch: item.branch || '',
      region: item.region || '',
      client_category: item.client_category || ''
    });
    setHierarchyDialogOpen(true);
  };

  const saveHierarchy = async () => {
    try {
      await axios.put(`${API}/admin/users/${hierarchyClientId}/hierarchy`, hierarchyForm, { withCredentials: true });
      toast.success('Bank hierarchy updated');
      setHierarchyDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error updating bank hierarchy:', err);
      toast.error(err.response?.data?.detail || 'Failed to update bank hierarchy');
    }
  };

  // Family management states
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [familyOwners, setFamilyOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerAccounts, setOwnerAccounts] = useState([]);
  const [familyLoading, setFamilyLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'family-management') {
      fetchFamilies();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Admin can see all users
      const usersRes = await axios.get(`${API}/admin/all-users`, config);
      const allUsers = usersRes.data;
      
      // Get clients data
      const clientsData = await axios.get(`${API}/clients`, config);
      
      // Merge data
      const clientsList = allUsers.filter(u => u.role === 'user').map(user => {
        const clientData = clientsData.data.find(c => c.id === user.id) || {};
        return { ...user, ...clientData };
      });
      
      setClients(clientsList.filter(c => c.user_type === 'client' || !c.user_type));
      setProspects(clientsList.filter(c => c.user_type === 'prospect'));
      
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies = async () => {
    setFamilyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/families`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilies(response.data.families || []);
    } catch (error) {
      console.error('Error fetching families:', error);
      toast.error('Failed to fetch families');
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleFamilySelect = async (family) => {
    setSelectedFamily(family);
    setFamilyOwners([]);
    setSelectedOwner(null);
    setOwnerAccounts([]);
    setFamilyLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/owners/family/${family.family_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyOwners(response.data.owners || []);
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error('Failed to fetch family owners');
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleOwnerSelect = async (owner) => {
    setSelectedOwner(owner);
    setOwnerAccounts([]);
    setFamilyLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/accounts/owner/${owner.owner_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwnerAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to fetch owner accounts');
    } finally {
      setFamilyLoading(false);
    }
  };

  const viewRiskProfile = async (clientId, hasProfile) => {
    if (!hasProfile) {
      navigate(`/questionnaire?user_id=${clientId}`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [profileRes, historyRes] = await Promise.all([
        axios.get(`${API}/questionnaire/user/${clientId}`, config),
        axios.get(`${API}/questionnaire/history/${clientId}`, config)
      ]);
      
      if (profileRes.data) {
        setClientRiskProfile(profileRes.data);
        setRiskProfileHistory(historyRes.data || []);
        setSelectedClientId(clientId);
        setRiskProfileDialogOpen(true);
      } else {
        toast.error('No risk profile found');
        navigate(`/questionnaire?user_id=${clientId}`);
      }
    } catch (error) {
      console.error('Risk profile fetch error:', error);
      toast.error('Failed to fetch risk profile');
    }
  };

  const viewPortfolioDetails = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/portfolios/client/${clientId}`, config);
      
      if (response.data && (response.data.holdings || response.data.total_market_value !== undefined)) {
        setClientPortfolio(response.data);
        setSelectedClientId(clientId);
        setPortfolioDialogOpen(true);
      } else {
        toast.error('No portfolio found for this client');
      }
    } catch (error) {
      console.error('Portfolio fetch error:', error);
      toast.error('Failed to fetch portfolio details');
    }
  };

  const generateRebalancingReport = async (clientId) => {
    setRebalanceLoading(true);
    setRebalanceDialogOpen(true);
    setSelectedClientId(clientId);
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.post(`${API}/portfolio-rebalancing/${clientId}`, {}, config);
      setRebalanceData(response.data);
    } catch (error) {
      console.error('Rebalancing error:', error);
      toast.error('Failed to generate rebalancing report');
      setRebalanceDialogOpen(false);
    } finally {
      setRebalanceLoading(false);
    }
  };

  const viewSocialProfile = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/social-profile/${clientId}`, config);
      
      if (response.data) {
        setSocialProfileData(response.data);
        setSocialProfileForm({
          education: response.data.education || '',
          travel_preferences: response.data.travel_preferences || '',
          sports_interests: response.data.sports_interests || '',
          philanthropy: response.data.philanthropy || '',
          lifestyle_indicators: response.data.lifestyle_indicators || '',
          social_media_activity: response.data.social_media_activity || '',
          hobbies: response.data.hobbies || '',
          professional_network: response.data.professional_network || ''
        });
      } else {
        setSocialProfileData(null);
        setSocialProfileForm({
          education: '',
          travel_preferences: '',
          sports_interests: '',
          philanthropy: '',
          lifestyle_indicators: '',
          social_media_activity: '',
          hobbies: '',
          professional_network: ''
        });
      }
      
      setSelectedClientId(clientId);
      setSocialProfileDialogOpen(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setSocialProfileData(null);
        setSocialProfileForm({
          education: '',
          travel_preferences: '',
          sports_interests: '',
          philanthropy: '',
          lifestyle_indicators: '',
          social_media_activity: '',
          hobbies: '',
          professional_network: ''
        });
        setSelectedClientId(clientId);
        setSocialProfileDialogOpen(true);
      } else {
        toast.error('Failed to fetch social profile');
      }
    }
  };

  const saveSocialProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        user_id: selectedClientId,
        ...socialProfileForm,
        updated_by_admin_id: user.id,
        updated_by_admin_name: user.full_name
      };

      await axios.post(`${API}/social-profile`, payload, config);
      
      toast.success('Social profile saved successfully');
      setSocialProfileDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Save social profile error:', error);
      toast.error('Failed to save social profile');
    }
  };

  const openThresholdsDialog = (clientId, clientName) => {
    setThresholdsClientId(clientId);
    setThresholdsClientName(clientName);
    setThresholdsDialogOpen(true);
  };

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(
      (item) =>
        (item.full_name && item.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.pan && item.pan.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const DataTable = ({ data, type }) => (
    <div className="space-y-4">
      {data.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>No {type}s found</p>
        </div>
      ) : (
        data.map((item) => (
          <Card key={item.id} className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#fffdf0] flex items-center justify-center">
                      <User className="w-6 h-6 text-ugmonk-dark" />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold text-lg">{item.full_name}</h3>
                      {item.email && <p className="text-gray-600 text-sm">{item.email}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
                    {(item.pan || item.ssn) && (
                      <div>
                        <span className="text-gray-500">{getIdentifierLabel(user?.market)}:</span>
                        <p className="text-gray-900">{item.ssn || item.pan}</p>
                      </div>
                    )}
                    {item.mobile && (
                      <div>
                        <span className="text-gray-500">Mobile:</span>
                        <p className="text-gray-900">{item.mobile}</p>
                      </div>
                    )}
                    {item.risk_profile && (
                      <div>
                        <span className="text-gray-500">Risk Profile:</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          item.risk_profile === 'Conservative' ? 'bg-[#fffdf0] text-ugmonk-dark' :
                          item.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-700' :
                          item.risk_profile === 'Not Assessed' ? 'bg-gray-100 text-gray-600' :
                          'bg-[#fffdf0] text-ugmonk-dark'
                        }`}>
                          {item.risk_profile}
                        </span>
                      </div>
                    )}
                    {(item.branch || item.region || item.client_category) && (
                      <div>
                        <span className="text-gray-500">Bank Hierarchy:</span>
                        <p className="text-gray-900">
                          {[item.branch, item.region, item.client_category].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
                    onClick={() => viewRiskProfile(item.id, item.questionnaire_completed)}
                  >
                    <TrendingUp size={14} className="mr-1" />
                    {item.questionnaire_completed ? 'View Risk Profile' : 'Assess Risk'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-[#1a1a1a] text-ugmonk-dark hover:bg-[#fffdf0]"
                    onClick={() => viewPortfolioDetails(item.id)}
                  >
                    View Details
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-purple-600 text-purple-600 hover:bg-purple-50"
                    onClick={() => generateRebalancingReport(item.id)}
                  >
                    <RefreshCw size={14} className="mr-1" />
                    Rebalancing
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-[#ffd87a] text-ugmonk-dark hover:bg-[#fffdf0]"
                    onClick={() => viewSocialProfile(item.id)}
                  >
                    Social Profile
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-600 hover:bg-gray-50"
                    onClick={() => openThresholdsDialog(item.id, item.full_name)}
                  >
                    <Settings size={14} className="mr-1" />
                    Thresholds
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-gray-400 text-gray-700 hover:bg-gray-50"
                    onClick={() => openHierarchyDialog(item)}
                  >
                    Edit Hierarchy
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
            Clients & Prospects
          </h1>
          <p className="text-gray-600">Manage all clients, prospects and family hierarchy</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="clients-prospects" className="data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />
              Clients & Prospects
            </TabsTrigger>
            <TabsTrigger value="family-management" className="data-[state=active]:bg-white">
              <Building className="w-4 h-4 mr-2" />
              Family Management
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clients-prospects" className="space-y-6">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, PAN, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-gray-300 h-9 text-sm"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Clients</h2>
                <DataTable data={filterData(clients)} type="client" />
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Prospects</h2>
                <DataTable data={filterData(prospects)} type="prospect" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="family-management" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Families List */}
              <Card className="border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Building className="w-5 h-5 text-ugmonk-dark" />
                    Families
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                  {familyLoading && !families.length ? (
                    <div className="space-y-2">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-20 rounded-lg bg-gray-100 animate-pulse" />
                      ))}
                    </div>
                  ) : families.length === 0 ? (
                    <p className="text-center py-8 text-gray-500">No families found</p>
                  ) : (
                    <div className="space-y-2">
                      {families.map((family) => (
                        <div
                          key={family.family_id}
                          onClick={() => handleFamilySelect(family)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedFamily?.family_id === family.family_id
                              ? 'border-[#ffd87a] bg-[#ffd87a]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-semibold text-gray-900">{family.family_name}</div>
                          <div className="text-sm text-gray-600">
                            {family.total_owners} owners · {marketFormatCurrency(family.total_aum || 0, user?.market)} AUM
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Owners List */}
              <Card className="border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <User className="w-5 h-5 text-ugmonk-dark" />
                    Owners
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                  {!selectedFamily ? (
                    <div className="text-center py-8 text-gray-500">
                      <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Select a family to view owners</p>
                    </div>
                  ) : familyLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a1a] mx-auto mb-3"></div>
                      <p className="text-gray-500">Loading owners...</p>
                    </div>
                  ) : familyOwners.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No owners found in {selectedFamily?.family_name}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {familyOwners.map((owner) => (
                        <div
                          key={owner.owner_id}
                          onClick={() => handleOwnerSelect(owner)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedOwner?.owner_id === owner.owner_id
                              ? 'border-[#ffd87a] bg-[#ffd87a]/5'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <div className="font-semibold text-gray-900 text-sm">{owner.owner_name}</div>
                          <div className="text-xs text-gray-600">{getIdentifierLabel(user?.market)}: {owner.ssn || owner.pan || 'N/A'}</div>
                          <div className="text-xs text-gray-600">Mobile: {owner.mobile || 'N/A'}</div>
                          <div className="text-xs text-ugmonk-dark">
                            AUM: {marketFormatCurrency(owner.total_aum || 0, user?.market)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Accounts List */}
              <Card className="border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-ugmonk-dark" />
                    Accounts ({ownerAccounts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                  {!selectedOwner ? (
                    <div className="text-center py-8 text-gray-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Select an owner to view accounts</p>
                    </div>
                  ) : familyLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd87a] mx-auto mb-3"></div>
                      <p className="text-gray-500">Loading accounts...</p>
                    </div>
                  ) : ownerAccounts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No accounts found for this owner</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {ownerAccounts.map((account) => (
                        <div
                          key={account.account_id}
                          className="p-3 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <div className="font-semibold text-gray-900 text-sm">{account.account_name}</div>
                          <div className="text-xs text-gray-600">Type: {account.account_type || 'Investment'}</div>
                          <div className="text-xs text-gray-600">Number: {account.account_number}</div>
                          {account.is_held_away && (
                            <div className="text-xs text-ugmonk-dark font-medium">⚠️ Held Away Account</div>
                          )}
                          <div className="text-xs text-ugmonk-dark font-semibold">
                            Value: {marketFormatCurrency(account.aum || account.total_value || 0, user?.market)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Risk Profile Dialog */}
        {riskProfileDialogOpen && clientRiskProfile && (
          <Dialog open={riskProfileDialogOpen} onOpenChange={setRiskProfileDialogOpen}>
            <DialogContent className="bg-white max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-2xl">Risk Profile & Investment Preferences</DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="risk-profile" className="space-y-6">
                <TabsList className="bg-gray-100">
                  <TabsTrigger value="risk-profile" className="data-[state=active]:bg-white">
                    Risk Profile
                  </TabsTrigger>
                  <TabsTrigger value="no-go-list" className="data-[state=active]:bg-white">
                    No Go List
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="risk-profile" className="space-y-6">
                  <Card className="border-ugmonk-yellow bg-[#fffdf0]">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Current Risk Profile</h3>
                          <p className="text-3xl font-bold text-ugmonk-dark mt-2">{clientRiskProfile.risk_profile}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            Score: {clientRiskProfile.risk_score} | Last updated: {new Date(clientRiskProfile.completed_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {riskProfileHistory.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Assessment History</h3>
                      <div className="space-y-2">
                        {riskProfileHistory.map((history, index) => (
                          <Card key={index} className="border-gray-200">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-center">
                                <div>
                                  <span className="font-medium text-gray-900">{history.risk_profile}</span>
                                  <span className="text-sm text-gray-500 ml-3">Score: {history.risk_score}</span>
                                </div>
                                <span className="text-sm text-gray-500">
                                  {new Date(history.completed_at).toLocaleDateString()}
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="no-go-list">
                  <NoGoList 
                    clientId={selectedClientId} 
                    clientName={clients.find(c => c.id === selectedClientId)?.full_name || 'Client'}
                  />
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        )}

        {/* Portfolio Dialog */}
        {portfolioDialogOpen && clientPortfolio && (
          <Dialog open={portfolioDialogOpen} onOpenChange={setPortfolioDialogOpen}>
            <DialogContent className="bg-white max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-2xl">Portfolio Details</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-900">Portfolio Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-ugmonk-dark">
                      {getCurrencySymbol(user?.market)}{(clientPortfolio.total_market_value || clientPortfolio.total_value || 0).toLocaleString()}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Total Portfolio Value</p>
                  </CardContent>
                </Card>

                {clientPortfolio.holdings && clientPortfolio.holdings.length > 0 && (
                  <>
                    <Card className="border-gray-200">
                      <CardHeader>
                        <CardTitle className="text-gray-900">Asset Allocation</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <PortfolioChart portfolio={clientPortfolio} />
                      </CardContent>
                    </Card>

                    <Card className="border-gray-200">
                      <CardHeader>
                        <CardTitle className="text-gray-900">Holdings</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left py-2 text-gray-700">Security</th>
                                <th className="text-left py-2 text-gray-700">Asset Class</th>
                                <th className="text-right py-2 text-gray-700">Quantity</th>
                                <th className="text-right py-2 text-gray-700">Value</th>
                                <th className="text-right py-2 text-gray-700">Allocation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientPortfolio.holdings.map((holding, index) => (
                                <tr key={index} className="border-b border-gray-100">
                                  <td className="py-3 text-gray-900">{holding.security_name || holding.security}</td>
                                  <td className="py-3 text-gray-600">{holding.asset_class || holding.type}</td>
                                  <td className="py-3 text-right text-gray-900">{holding.quantity}</td>
                                  <td className="py-3 text-right text-gray-900">
                                    {getCurrencySymbol(user?.market)}{(holding.market_value || holding.value || 0).toLocaleString()}
                                  </td>
                                  <td className="py-3 text-right text-gray-900">
                                    {((holding.market_value || holding.value || 0) / (clientPortfolio.total_market_value || clientPortfolio.total_value || 1) * 100).toFixed(2)}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Social Profile Dialog */}
        {socialProfileDialogOpen && (
          <Dialog open={socialProfileDialogOpen} onOpenChange={setSocialProfileDialogOpen}>
            <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-2xl">Social Profile</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-700">Education</Label>
                  <Input
                    value={socialProfileForm.education}
                    onChange={(e) => setSocialProfileForm({ ...socialProfileForm, education: e.target.value })}
                    className="bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Travel Preferences</Label>
                  <Input
                    value={socialProfileForm.travel_preferences}
                    onChange={(e) => setSocialProfileForm({ ...socialProfileForm, travel_preferences: e.target.value })}
                    className="bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Sports Interests</Label>
                  <Input
                    value={socialProfileForm.sports_interests}
                    onChange={(e) => setSocialProfileForm({ ...socialProfileForm, sports_interests: e.target.value })}
                    className="bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Philanthropy</Label>
                  <Input
                    value={socialProfileForm.philanthropy}
                    onChange={(e) => setSocialProfileForm({ ...socialProfileForm, philanthropy: e.target.value })}
                    className="bg-white border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Hobbies</Label>
                  <Input
                    value={socialProfileForm.hobbies}
                    onChange={(e) => setSocialProfileForm({ ...socialProfileForm, hobbies: e.target.value })}
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setSocialProfileDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveSocialProfile} className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                    Save Profile
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Rebalancing Dialog */}
        {rebalanceDialogOpen && (
          <Dialog open={rebalanceDialogOpen} onOpenChange={setRebalanceDialogOpen}>
            <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-2xl">Portfolio Rebalancing Report</DialogTitle>
              </DialogHeader>
              {rebalanceLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
                </div>
              ) : rebalanceData ? (
                <div className="space-y-6">
                  <Card className="border-gray-200 bg-gray-50">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-gray-900 mb-4">Current Allocation vs Target</h3>
                      <div className="space-y-2">
                        {Object.entries(rebalanceData.current_allocation || {}).map(([asset, percentage]) => (
                          <div key={asset} className="flex justify-between items-center">
                            <span className="text-gray-700">{asset}</span>
                            <div className="flex gap-4">
                              <span className="text-gray-900 font-medium">{percentage}%</span>
                              <span className="text-gray-500">→</span>
                              <span className="text-ugmonk-dark font-medium">
                                {rebalanceData.target_allocation?.[asset]}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  {rebalanceData.recommendations && (
                    <Card className="border-gray-200">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-gray-900 mb-4">Rebalancing Actions</h3>
                        <div className="prose text-gray-700 whitespace-pre-wrap">
                          {rebalanceData.recommendations}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No rebalancing data available
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* Portfolio Thresholds Dialog */}
        {thresholdsDialogOpen && (
          <Dialog open={thresholdsDialogOpen} onOpenChange={setThresholdsDialogOpen}>
            <DialogContent className="bg-white max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-2xl">Portfolio Thresholds</DialogTitle>
              </DialogHeader>
              <PortfolioThresholds
                clientId={thresholdsClientId}
                clientName={thresholdsClientName}
                onClose={() => setThresholdsDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Bank Hierarchy Edit Dialog (branch/region/client_category) */}
        <Dialog open={hierarchyDialogOpen} onOpenChange={setHierarchyDialogOpen}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Edit Bank Hierarchy</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-900 font-medium mb-1 block">Branch</Label>
                <Input
                  value={hierarchyForm.branch}
                  onChange={(e) => setHierarchyForm(prev => ({ ...prev, branch: e.target.value }))}
                  placeholder="e.g. Mumbai - BKC"
                />
              </div>
              <div>
                <Label className="text-gray-900 font-medium mb-1 block">Region</Label>
                <Input
                  value={hierarchyForm.region}
                  onChange={(e) => setHierarchyForm(prev => ({ ...prev, region: e.target.value }))}
                  placeholder="e.g. West"
                />
              </div>
              <div>
                <Label className="text-gray-900 font-medium mb-1 block">Client Category</Label>
                <Input
                  value={hierarchyForm.client_category}
                  onChange={(e) => setHierarchyForm(prev => ({ ...prev, client_category: e.target.value }))}
                  placeholder="e.g. HNI, UHNI, APEX"
                />
              </div>
              <Button onClick={saveHierarchy} className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminClientsProspects;
